import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ProtectedLayout from "@/layouts/ProtectedLayout";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Package, Calendar, Ruler, Clock, ArrowRight, Sparkles } from "lucide-react";
import { GlassCard } from "@/components/common/GlassCard";
import { CTAButton } from "@/components/common/CTAButton";

const statsCards = [
  {
    label: "Active Orders",
    value: "2",
    icon: Package,
    color: "bg-primary/10 text-primary",
  },
  {
    label: "Completed",
    value: "12",
    icon: Package,
    color: "bg-green-100 text-green-600",
  },
  {
    label: "Next Pickup",
    value: "Tomorrow",
    icon: Calendar,
    color: "bg-blue-100 text-blue-600",
  },
  {
    label: "Avg. Turnaround",
    value: "22 hours",
    icon: Clock,
    color: "bg-accent text-primary",
  },
];

const recentOrders = [
  { id: "ORD-1234", items: 3, status: "in_progress" as const, date: "Dec 15, 2024" },
  { id: "ORD-1233", items: 2, status: "confirmed" as const, date: "Dec 14, 2024" },
  { id: "ORD-1232", items: 1, status: "completed" as const, date: "Dec 10, 2024" },
];

const measurements = [
  { label: "Bust", value: "92 cm" },
  { label: "Waist", value: "76 cm" },
  { label: "Hip", value: "98 cm" },
  { label: "Shoulder", value: "38 cm" },
];

export default function CustomerDashboardPage() {
  const content = (
    <div className="space-y-8 container mx-auto px-4 md:px-6 py-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-foreground">
            Welcome back!
          </h1>
          <p className="mt-1 text-muted-foreground">
            Manage your orders and schedule pickups
          </p>
        </div>
        <CTAButton href="/customer/orders/new" size="sm">
          <Sparkles className="h-4 w-4 mr-2" />
          New Order
        </CTAButton>
      </motion.div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statsCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <GlassCard className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-2xl md:text-3xl font-display font-semibold text-foreground mt-1">
                    {stat.value}
                  </p>
                </div>
                <div className={`h-12 w-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </GlassCard>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-lg font-semibold text-foreground">
                Recent Orders
              </h2>
              <Link
                to="/customer/orders"
                className="text-sm text-primary font-medium hover:text-primary/80 inline-flex items-center gap-1"
              >
                View All
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="space-y-3">
              {recentOrders.map((order) => (
                <Link
                  key={order.id}
                  to={`/customer/orders/${order.id}`}
                  className="flex items-center justify-between p-4 rounded-xl border border-border hover:bg-secondary/50 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{order.id}</p>
                    <p className="text-sm text-muted-foreground">
                      {order.items} item{order.items > 1 ? "s" : ""} · {order.date}
                    </p>
                  </div>
                  <StatusBadge status={order.status} />
                </Link>
              ))}
            </div>
          </GlassCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-lg font-semibold text-foreground">
                Your Measurements
              </h2>
              <Link
                to="/customer/measurements"
                className="text-sm text-primary font-medium hover:text-primary/80 inline-flex items-center gap-1"
              >
                View Details
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="flex items-center gap-4 p-4 rounded-xl bg-accent/50 border border-primary/10 mb-5">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Ruler className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-foreground">Measurements on File</p>
                <p className="text-sm text-muted-foreground">
                  Last updated: Dec 1, 2024
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {measurements.map((measurement) => (
                <div
                  key={measurement.label}
                  className="p-4 rounded-xl border border-border bg-white"
                >
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    {measurement.label}
                  </p>
                  <p className="font-display text-lg font-semibold text-foreground mt-1">
                    {measurement.value}
                  </p>
                </div>
              ))}
            </div>
          </GlassCard>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <GlassCard className="p-6">
          <h2 className="font-display text-lg font-semibold text-foreground mb-6">
            Upcoming Pickups
          </h2>
          <div className="space-y-3">
            <div className="flex items-center gap-4 p-4 rounded-xl border border-border bg-white">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Calendar className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-foreground">Tomorrow, Dec 17</p>
                <p className="text-sm text-muted-foreground">2:00 PM - 4:00 PM</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl border-border"
              >
                Reschedule
              </Button>
            </div>
          </div>
        </GlassCard>
      </motion.div>
    </div>
  );

  // Wrap with protected layout to show header and gate by auth
  return <ProtectedLayout>{content}</ProtectedLayout>;
}